import * as React from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react';
import { cn } from '@/utils/styles';
import useCarouselDots from './useCarouselDots';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface BlurOverlayProps {
  className?: string;
  inverted?: boolean;
}

interface CarouselDotsProps {
  className?: string;
  api: EmblaCarouselType | undefined;
}

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  showDots?: boolean;
  showMasks?: boolean;
  maskClassName?: string;
  dotsClassName?: string;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = 'horizontal',
      opts,
      setApi,
      plugins,
      className,
      showDots,
      showMasks,
      maskClassName,
      dotsClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === 'horizontal' ? 'x' : 'y',
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on('reInit', onSelect);
      api.on('select', onSelect);

      return () => {
        api?.off('select', onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}>
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn('relative flex flex-col', className)}
          role="region"
          aria-roledescription="carousel"
          {...props}>
          {showMasks ? (
            <>
              <BlurOverlay
                className={cn(
                  'left-0 z-30 w-[50px] -translate-y-[10%] lg:w-[400px]',
                  maskClassName,
                )}
              />
              <BlurOverlay
                className={cn(
                  'right-0 top-0 z-30 w-[50px] -translate-y-[10%] lg:w-[400px]',
                  maskClassName,
                )}
                inverted
              />
            </>
          ) : null}

          {children}

          {showDots ? (
            <CarouselDots api={api} className={dotsClassName} />
          ) : null}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="size-full overflow-hidden">
      <div
        ref={ref}
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className,
        )}
        {...props}
      />
    </div>
  );
});
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel();

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        'min-w-0 shrink-0 grow-0',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className,
      )}
      {...props}
    />
  );
});
CarouselItem.displayName = 'CarouselItem';

const CarouselDots = ({ api, className }: CarouselDotsProps) => {
  const { onDotButtonClick, selectedIndex } = useCarouselDots(api!);
  return (
    <ul
      className={cn(
        'flex w-full flex-row items-center justify-center gap-3 md:mt-2 lg:gap-2',
        className,
      )}>
      {Array.from({ length: api?.scrollSnapList().length ?? 0 }, (_, i) => (
        <li key={i}>
          <CarouselDot
            isSelected={i === selectedIndex}
            onClick={() => onDotButtonClick(i)}
          />
        </li>
      ))}
    </ul>
  );
};

const CarouselDot = ({
  onClick,
  isSelected,
}: {
  onClick?: () => void;
  isSelected?: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex h-1 w-6 rounded-full bg-neutral-700 transition-colors duration-100 lg:h-2 lg:w-8',
        {
          '!bg-azure-700': isSelected,
        },
      )}
    />
  );
};

const BlurOverlay = ({ className, inverted }: BlurOverlayProps) => {
  const gradientDirection = inverted ? 'to left' : 'to right';
  return (
    <div
      style={{
        maskImage: `linear-gradient(${gradientDirection}, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0))`,
      }}
      className={cn(
        'pointer-events-none absolute h-[120%] bg-neutral-1000 backdrop-blur-2xl',
        className,
      )}
    />
  );
};

export { type CarouselApi, Carousel, CarouselContent, CarouselItem };
